import { ConnectionAdapter, ReadyState, Response, Socket } from '@quan/websocket';
import { SocketOptions } from '@quan/websocket/typings/interfaces';
import { logger } from '@quan/logger';
import { delay, noop, incrementId, global, debounce } from '@planjs/utils';
import { Websocket_Cmd } from '@quan/proto-api-client';

import { BrickNotLoginErrCode, NotLoginError } from './constants';

/**
 * init 重试次数
 */
const MaxRetryInit = 10;
const InitTimeOut = 5000;
const PingInterval = 20000;
const RetryInitTimeCut = 5000;
/**
 * 保证每次init 序号唯一
 */
const initSeqFN = incrementId();

/**
 * 获取websocket 实例, 返回的实例会等待websocket init 完成 （onOpen 在拿到实例之后注册是无效的）
 * @param initMsg 初始化参数 biz_type 参考 core.PushType
 * @param adapter @quan/websocket
 * @param handlerMsg 会过滤掉 pong 的消息 返回 websocket.Message
 * @param sockOptions
 * @summary 如果是 init ping 失败（ws state is open）的情况这里会自动重试，close的重试需要业务自己实现
 *          init 成功才会返回socket instance
 * @example
 * import { ConnectionBrowserSocketClient, Socket } from '@quan/websocket';
 * getSocketInstance({
 *     init_msg: {
 *        biz_type: Core_PushType.PushTypeQMsg,
 *        conn_type: Websocket_ConnType.ConnTypeQMsg,
 *        biz_id: '',
 *      },
 *    },
 *    new ConnectionBrowserSocketClient(),
 *    msg => socketEventHandler(msg)
 * ).then(socket => {
 *    const handleClose = debounce(() => {
 *      // TODO 这里本来可以判断 close 的code 是否1000 判断是否正常退出，排除掉reOpen的情况，但是后端不支持，所以 onOpen cancel一下，或者先取消onClose事件
 *      ws.reOpen();
 *    }, 2000);
 *    socket.onClose(handleClose)
 *    socket.onOpen(() => handleClose.cancel())
 * }).catch(err => {
 *   // 这里按需重试，初始化直接报错，差不多算没救了，重试的话，重新调用 getSocketInstance
 * })
 */
export async function getSocketInstance(
  initMsg: Omit<websocket.Message, 'cmd_id'>,
  adapter: ConnectionAdapter,
  handlerMsg: (res: websocket.Message) => void,
  sockOptions?: SocketOptions,
): Promise<Socket> {
  let url = `${(process.env.WSHost! || process.env.baseApi! || env?.baseApi)
    .replace(/https:/, 'wss:')
    .replace(/http:/, 'ws:')}/ws`;
  if (url.startsWith('//')) {
    // 兼容如果是 web worker
    url = (global?.location?.protocol === 'https:' ? 'wss:' : 'ws:') + url;
  }
  console.warn('ws url', url);

  if (!initMsg?.init_msg?.session_id) {
    return Promise.reject(NotLoginError);
  }

  const sockInstance = new Socket(adapter, {
    url,
    autoOpen: true,
    ...sockOptions,
  });

  let initSeq = initSeqFN();

  const lockManager: {
    p: Promise<void> | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _resolve: (val?: any) => void | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _reject: (val?: any) => void | null;
    lock: () => Promise<void>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    unlock: (val?: any) => void | null;
    timerID: number;
  } = {
    p: null,
    _resolve: noop,
    _reject: noop,
    timerID: 0,
    unlock(res) {
      this._resolve && this._resolve(res);
      clearTimeout(this.timerID);
    },
    lock() {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      return (this.p = new Promise((resolve, reject) => {
        self._resolve = resolve;
        self._reject = reject;
        self.timerID = setTimeout(() => {
          self._reject && self._reject(new Error('init timeout'));
        }, InitTimeOut) as unknown as number;
      }));
    },
  };

  // eslint-disable-next-line prefer-rest-params
  const args = Array.prototype.slice.call(arguments, 0, arguments.length) as Parameters<
    typeof getSocketInstance
  >;

  // 处理如果服务端没有返回 pong 就重新初始化, 链接可能已经没用了
  const handlePongTimeout = debounce(() => {
    if (sockInstance.readyState === ReadyState.OPEN) {
      sockInstance.reOpen();
      logger.error({
        key: 'common_ws_error',
        type: 'websocket_error',
        name: 'pong_timeout',
        message: '',
        metrics: {
          metrics_type: 'count',
          values: '1',
        },
      });
    }
  }, PingInterval / 2);

  return new Promise((resolve, reject) => {
    let retryInit = 0;
    // 初始化ws
    const init = (): Promise<Response | Socket> => {
      if (!initMsg?.init_msg?.session_id) {
        return Promise.reject(NotLoginError);
      }
      return sockInstance
        .send({
          ...initMsg,
          init_msg: {
            device_id: env?.deviceSid,
            ...initMsg?.init_msg,
          },
          sequence: initSeq,
          cmd_id: Websocket_Cmd.CMD_INIT,
        } as websocket.Message)
        .then((res) =>
          lockManager.lock().then(() => {
            retryInit = 0;
            return res;
          }),
        )
        .catch((err) => {
          // 当ws没有连接的时候重试
          if (sockInstance.readyState !== ReadyState.OPEN) {
            return new Promise((r) => {
              sockInstance.removeListener();
              sockInstance.close();
              setTimeout(() => r(getSocketInstance(...args)), RetryInitTimeCut);
            });
          }
          // 当sid失效的时候
          if (err?.ret_code === BrickNotLoginErrCode || err === NotLoginError) {
            return Promise.reject(NotLoginError);
          }
          // 重新链接
          retryInit++;
          if (retryInit > MaxRetryInit) {
            logger.error({
              key: 'common_ws_error',
              type: 'websocket_error',
              name: 'init_timeout',
              message: `websocket CMD_INIT failure, retry [${retryInit}]`,
              ext: { initMsg, err },
              metrics: {
                metrics_type: 'count',
                values: '1',
              },
            });
            return Promise.reject(new Error(`Websocket init retry more than ${retryInit} times`));
          }
          // ws正常链接重试失败等待一段时间重试
          return delay<void>(retryInit * RetryInitTimeCut)
            .then(() => {
              initSeq = initSeqFN();
            })
            .then(init);
        });
    };

    // 初始化第一次失败就会自动走入重试逻辑
    const timer = setTimeout(() => {
      resolve(init().then(() => sockInstance));
    }, RetryInitTimeCut);

    // 当成功打开,开始ping，返回实例
    sockInstance.onOpen(() => {
      clearTimeout(timer);
      init()
        .then(() => {
          handlePongTimeout.cancel();
          return sockInstance.ping(PingInterval, {
            cmd_id: Websocket_Cmd.CMD_PING,
          });
        })
        .then(() => resolve(sockInstance), reject);
    });

    // 监听ping
    sockInstance.onPing((pingRes) => {
      pingRes
        .then(() => {
          handlePongTimeout();
        })
        // ping 失败上报
        .catch((err) => {
          // 如果是连接成功，发送失败则重新连接一下
          if (sockInstance.readyState === ReadyState.OPEN) {
            sockInstance.reOpen();
          }
          logger.error({
            key: 'common_ws_error',
            type: 'websocket_error',
            name: 'ping_error',
            message: '',
            metrics: {
              metrics_type: 'count',
              values: '1',
            },
            ext: { err },
          });
        });
    });

    // 消息处理
    sockInstance.onMessage((res) => {
      handlePongTimeout.cancel();
      try {
        // TODO 暂时当string 返回
        const data: websocket.Message = JSON.parse(String(res.message));
        // 过滤掉init消息
        if (data.cmd_id === Websocket_Cmd.CMD_INIT) {
          if (data.sequence === initSeq) {
            if (data.ret_code === 0) {
              lockManager.unlock();
            } else {
              if (data.ret_code !== BrickNotLoginErrCode) {
                logger.error({
                  key: 'common_ws_error',
                  type: 'websocket_error',
                  name: 'init_error',
                  message: '',
                  ext: { data, initMsg },
                  metrics: {
                    metrics_type: 'count',
                    values: '1',
                  },
                });
              }
              lockManager._reject(data);
            }
          }
        } else if (data.cmd_id === Websocket_Cmd.CMD_PONG) {
          //
        } else {
          // 非心跳包
          handlerMsg(data);
        }
      } catch (e: Error | any) {
        logger.captureException(e);
      }
    });

    sockInstance.onClose((res) => {
      handlePongTimeout.cancel();
      // 非正常关闭上报
      if (!res.OK) {
        logger.log({
          key: 'common_ws_error',
          type: 'websocket_error',
          name: 'abnormal_close',
          message: '',
          ext: { res },
          metrics: {
            metrics_type: 'count',
            values: '1',
          },
        });
      }
      console.log('getSocketInstance socket onClose', res);
    });

    // TODO 这里错误后端不是标准的ws框架，报错有问题
    sockInstance.onError((err) => {
      // 当正在打开，产生的onError就不上报了
      if (sockInstance.readyState !== ReadyState.OPEN) {
        // TODO 错误要分类
        logger.error({
          key: 'common_ws_error',
          type: 'websocket_error',
          name: 'error',
          message: '',
          ext: { err },
          metrics: {
            metrics_type: 'count',
            values: '1',
          },
        });
      }
    });
  });
}
